import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite, list, type Invoice } from '../lib/api'
import { useAuth } from '../auth'
import { Empty, Money, PageHead, Skel, Stat } from '../components/ui'
import { thaiDate } from '../lib/format'

/**
 * Partner / reseller commission ledger (part 142).
 *
 * The rate is basis points of the invoice's net value: 250 bp is 2.5%. The
 * interface converts the percent the user types into integer basis points
 * before sending, and all amounts stay integer satang end to end — no float
 * ever decides how much a partner is owed.
 */

type PartnerStatus = 'active' | 'paused'
type CommissionStatus = 'accrued' | 'approved' | 'paid'

interface Partner {
  id: string
  code: string
  name: string
  commission_bp: number
  currency: string
  status: PartnerStatus
}

interface CommissionRow {
  id: string
  partner_id: string
  partner_code: string
  partner_name: string
  invoice_id: string
  invoice_number: string
  base_satang: number
  commission_bp: number
  amount_satang: number
  status: CommissionStatus
  created_at: string
}

interface Accrual {
  invoice_id: string
  invoice_number: string
  base_satang: number
  created: number
  skipped: number
  rows: CommissionRow[]
}

interface StatementLine {
  partner_id: string
  partner_code: string
  partner_name: string
  count: number
  accrued_satang: number
  approved_satang: number
  paid_satang: number
  total_satang: number
}

interface Statement {
  lines: StatementLine[]
  count: number
  accrued_satang: number
  approved_satang: number
  paid_satang: number
  total_satang: number
}

interface PartnerDraft {
  id: string
  code: string
  name: string
  percent: string
  currency: string
  status: PartnerStatus
}

const EMPTY_DRAFT: PartnerDraft = { id: '', code: '', name: '', percent: '2.5', currency: 'THB', status: 'active' }

const PARTNER_LABEL: Record<PartnerStatus, string> = { active: 'ใช้งาน', paused: 'พักไว้' }
const COMMISSION_LABEL: Record<CommissionStatus, string> = {
  accrued: 'ค้างจ่าย', approved: 'อนุมัติแล้ว', paid: 'จ่ายแล้ว',
}
const COMMISSION_TONE: Record<CommissionStatus, string> = {
  accrued: 'var(--color-amber)',
  approved: 'var(--color-gold)',
  paid: 'var(--color-emerald-soft)',
}

/** "7.25" -> 725 bp. Rejects more than two decimals instead of rounding them. */
function parseCommissionBP(raw: string): number | null {
  const s = raw.trim().replace(/%/g, '').replace(/,/g, '')
  if (!/^\d{1,2}(\.\d{0,2})?$/.test(s)) return null
  const [whole, frac = ''] = s.split('.')
  const bp = Number(whole) * 100 + Number(`${frac}00`.slice(0, 2))
  if (!Number.isSafeInteger(bp) || bp < 0 || bp > 5000) return null
  return bp
}

/** 725 -> "7.25%", 700 -> "7%", 705 -> "7.05%". */
function commissionPercent(bp: number): string {
  const v = Math.max(0, Math.trunc(bp))
  return `${(v / 100).toFixed(2).replace(/\.00$/, '').replace(/(\.\d)0$/, '$1')}%`
}

/** 725 -> "7.25" for the edit field, without trailing zeros. */
function percentInput(bp: number): string {
  return (Math.max(0, Math.trunc(bp)) / 100).toFixed(2).replace(/\.?0+$/, '')
}

export function Partners() {
  const { me } = useAuth()
  const canManage = me?.role === 'owner' || me?.role === 'admin' || me?.role === 'audit_manager'
  const canPay = me?.role === 'owner' || me?.role === 'admin'
  const qc = useQueryClient()

  const partnersQ = useQuery({ queryKey: ['partners'], queryFn: () => list<Partner>('/v1/partners') })
  const commissionsQ = useQuery({
    queryKey: ['partner-commissions'],
    queryFn: () => list<CommissionRow>('/v1/partners/commissions?limit=200'),
  })
  const statementQ = useQuery({
    queryKey: ['partner-statement'],
    queryFn: () => api<Statement>('/v1/partners/statement'),
  })

  const partners = partnersQ.data ?? []
  const commissions = commissionsQ.data ?? []

  const [draft, setDraft] = useState<PartnerDraft>(EMPTY_DRAFT)
  const [formErr, setFormErr] = useState('')
  const [invoiceID, setInvoiceID] = useState('')
  const [accrueErr, setAccrueErr] = useState('')
  const [accrual, setAccrual] = useState<Accrual | null>(null)

  const invoicesQ = useQuery({ queryKey: ['invoices'], queryFn: () => list<Invoice>('/v1/invoices?limit=50') })
  const invoices = invoicesQ.data ?? []

  const savePartner = useMutation({
    mutationFn: (body: { id: string; code: string; name: string; commission_bp: number; currency: string; status: PartnerStatus }) =>
      apiWrite<Partner>('/v1/partners', 'POST', body),
    onSuccess: () => {
      setDraft(EMPTY_DRAFT)
      setFormErr('')
      void qc.invalidateQueries({ queryKey: ['partners'] })
      void qc.invalidateQueries({ queryKey: ['partner-statement'] })
      void qc.invalidateQueries({ queryKey: ['partner-commissions'] })
      setAccrual(null)
    },
    onError: (e) => setFormErr((e as Error).message),
  })

  const accrue = useMutation({
    mutationFn: (id: string) => apiWrite<Accrual>('/v1/partners/commissions/accrue', 'POST', { invoice_id: id }),
    onSuccess: (res) => {
      setAccrual(res)
      setAccrueErr('')
      void qc.invalidateQueries({ queryKey: ['partner-commissions'] })
      void qc.invalidateQueries({ queryKey: ['partner-statement'] })
    },
    onError: (e) => { setAccrual(null); setAccrueErr((e as Error).message) },
  })

  const markPaid = useMutation({
    mutationFn: (id: string) => apiWrite<CommissionRow>(`/v1/partners/commissions/${id}/paid`, 'POST'),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['partner-commissions'] })
      void qc.invalidateQueries({ queryKey: ['partner-statement'] })
    },
    onError: (e) => setAccrueErr((e as Error).message),
  })

  const statement = statementQ.data

  /**
   * The statement totals are recomputed here from the same rows the table
   * shows, so the header and the list can never disagree while the dedicated
   * endpoint is loading.
   */
  const totals = useMemo(() => {
    const lines = new Map<string, StatementLine>()
    for (const c of commissions) {
      const line = lines.get(c.partner_id) ?? {
        partner_id: c.partner_id, partner_code: c.partner_code, partner_name: c.partner_name,
        count: 0, accrued_satang: 0, approved_satang: 0, paid_satang: 0, total_satang: 0,
      }
      line.count += 1
      line.total_satang += c.amount_satang
      if (c.status === 'accrued') line.accrued_satang += c.amount_satang
      if (c.status === 'approved') line.approved_satang += c.amount_satang
      if (c.status === 'paid') line.paid_satang += c.amount_satang
      lines.set(c.partner_id, line)
    }
    const rows = Array.from(lines.values()).sort((a, b) => a.partner_code.localeCompare(b.partner_code))
    const sum = (pick: (l: StatementLine) => number) => rows.reduce((s, l) => s + pick(l), 0)
    return {
      lines: rows,
      count: rows.reduce((s, l) => s + l.count, 0),
      accrued: sum((l) => l.accrued_satang),
      approved: sum((l) => l.approved_satang),
      paid: sum((l) => l.paid_satang),
      total: sum((l) => l.total_satang),
    }
  }, [commissions])

  const totalsView = statement
    ? {
        lines: statement.lines,
        count: statement.count,
        accrued: statement.accrued_satang,
        approved: statement.approved_satang,
        paid: statement.paid_satang,
        total: statement.total_satang,
      }
    : totals

  const activePartners = partners.filter((p) => p.status === 'active').length
  const selectedInvoice = invoiceID || invoices[0]?.id || ''

  function startEdit(p: Partner) {
    setDraft({
      id: p.id, code: p.code, name: p.name,
      percent: percentInput(p.commission_bp), currency: p.currency, status: p.status,
    })
    setFormErr('')
  }

  function submitPartner() {
    const bp = parseCommissionBP(draft.percent)
    if (draft.code.trim() === '' || draft.name.trim() === '') {
      setFormErr('กรอกโค้ดและชื่อพาร์ทเนอร์')
      return
    }
    if (bp === null) {
      setFormErr('ค่าคอมมิชชันต้องเป็นเปอร์เซ็นต์ 0–50 และมีทศนิยมไม่เกิน 2 ตำแหน่ง')
      return
    }
    setFormErr('')
    savePartner.mutate({
      id: draft.id,
      code: draft.code.trim().toUpperCase(),
      name: draft.name.trim(),
      commission_bp: bp,
      currency: draft.currency,
      status: draft.status,
    })
  }

  return (
    <>
      <PageHead
        title="พาร์ทเนอร์และค่าคอมมิชชัน"
        sub="ทะเบียนพาร์ทเนอร์ · บันทึกค่าคอมมิชชันจากยอดสุทธิของใบแจ้งหนี้ · จ่ายเป็นจำนวนเต็มสตางค์"
        actions={<span className="chip">{partners.length} พาร์ทเนอร์</span>}
      />

      <div className="grid-3" style={{ marginBottom: 16 }}>
        <Stat label="พาร์ทเนอร์ทั้งหมด" value={partners.length} unit="ราย" foot={`ใช้งาน ${activePartners} ราย`} />
        <Stat label="ค่าคอมมิชชันค้างจ่าย" value={<Money satang={totalsView.accrued} compact />} foot={`${totalsView.count} รายการในบัญชี`} />
        <Stat label="จ่ายแล้วสะสม" value={<Money satang={totalsView.paid} compact />} foot="นับเฉพาะรายการที่ทำเครื่องหมายจ่ายแล้ว" />
      </div>

      <div className="grid-main">
        <div className="card card-pad">
          <div className="row">
            <span className="card-label">ทะเบียนพาร์ทเนอร์</span>
            <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>อัตราต่อหน่วยของยอดสุทธิ (ก่อน VAT)</span>
          </div>
          {partnersQ.isLoading ? (
            <div style={{ marginTop: 14 }}><Skel h={180} /></div>
          ) : partnersQ.isError ? (
            <div className="form-error" style={{ marginTop: 14 }}>โหลดทะเบียนพาร์ทเนอร์ไม่สำเร็จ — ลองรีเฟรชหน้าอีกครั้ง</div>
          ) : partners.length === 0 ? (
            <div style={{ marginTop: 14 }}>
              <Empty title="ยังไม่มีพาร์ทเนอร์" body="เพิ่มพาร์ทเนอร์รายแรกเพื่อเริ่มบันทึกค่าคอมมิชชันจากใบแจ้งหนี้" />
            </div>
          ) : (
            <div className="table-wrap" style={{ marginTop: 12 }}>
              <table className="data">
                <thead>
                  <tr><th>โค้ด</th><th>ชื่อ</th><th className="num">ค่าคอมมิชชัน</th><th>สกุลเงิน</th><th>สถานะ</th><th /></tr>
                </thead>
                <tbody>
                  {partners.map((p) => (
                    <tr key={p.id}>
                      <td className="mono">{p.code}</td>
                      <td>{p.name}</td>
                      <td className="num tnum">{commissionPercent(p.commission_bp)}</td>
                      <td className="mono dim">{p.currency}</td>
                      <td>
                        <span className="chip" style={{
                          color: p.status === 'active' ? 'var(--color-emerald-soft)' : 'var(--color-amber)',
                          borderColor: 'currentColor',
                        }}>
                          {PARTNER_LABEL[p.status] ?? p.status}
                        </span>
                      </td>
                      <td>
                        <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                          {canManage && (
                            <button className="btn btn-ghost btn-sm" onClick={() => startEdit(p)}>แก้ไข</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="card card-pad">
          <div className="card-label">{draft.id ? `แก้ไขพาร์ทเนอร์ · ${draft.code}` : 'เพิ่มพาร์ทเนอร์'}</div>
          <div className="dim" style={{ fontSize: 11.5, marginTop: 8, lineHeight: 1.8 }}>
            โค้ดใช้ตัวพิมพ์ใหญ่ A–Z, ตัวเลข และ - เท่านั้น · อัตราสูงสุด 50%
          </div>
          <form style={{ marginTop: 12 }} onSubmit={(e) => { e.preventDefault(); if (canManage) submitPartner() }}>
            <div className="row" style={{ gap: 10 }}>
              <div className="field" style={{ flex: 1 }}>
                <label htmlFor="p-code">โค้ดพาร์ทเนอร์</label>
                <input id="p-code" className="input mono" value={draft.code} disabled={!canManage || draft.id !== ''}
                  maxLength={24} placeholder="ACME-01"
                  onChange={(e) => setDraft({ ...draft, code: e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, '').slice(0, 24) })} />
              </div>
              <div className="field" style={{ flex: 1 }}>
                <label htmlFor="p-status">สถานะ</label>
                <select id="p-status" className="select" value={draft.status} disabled={!canManage}
                  onChange={(e) => setDraft({ ...draft, status: e.target.value as PartnerStatus })}>
                  <option value="active">ใช้งาน</option>
                  <option value="paused">พักไว้</option>
                </select>
              </div>
            </div>
            <div className="field">
              <label htmlFor="p-name">ชื่อพาร์ทเนอร์</label>
              <input id="p-name" className="input" value={draft.name} disabled={!canManage} maxLength={200}
                placeholder="บริษัท เอซีเอ็ม จำกัด" onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
            </div>
            <div className="row" style={{ gap: 10 }}>
              <div className="field" style={{ flex: 1 }}>
                <label htmlFor="p-percent">ค่าคอมมิชชัน (%)</label>
                <input id="p-percent" className="input mono" inputMode="decimal" value={draft.percent} disabled={!canManage}
                  placeholder="2.5" onChange={(e) => setDraft({ ...draft, percent: e.target.value.replace(/[^0-9.]/g, '').slice(0, 5) })} />
              </div>
              <div className="field" style={{ flex: 1 }}>
                <label htmlFor="p-currency">สกุลเงิน</label>
                <select id="p-currency" className="select" value={draft.currency} disabled={!canManage}
                  onChange={(e) => setDraft({ ...draft, currency: e.target.value })}>
                  <option value="THB">THB</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                </select>
              </div>
            </div>
            <div className="row" style={{ gap: 10 }}>
              <button className="btn btn-primary" type="submit" disabled={!canManage || savePartner.isPending} style={{ flex: 1 }}>
                {savePartner.isPending ? 'กำลังบันทึก…' : draft.id ? 'บันทึกการแก้ไข' : 'เพิ่มพาร์ทเนอร์'}
              </button>
              {draft.id !== '' && (
                <button className="btn btn-ghost" type="button" onClick={() => { setDraft(EMPTY_DRAFT); setFormErr('') }}>
                  ยกเลิก
                </button>
              )}
            </div>
          </form>
          {!canManage && <div className="dim" style={{ fontSize: 11.5, marginTop: 10 }}>เฉพาะเจ้าของ ผู้ดูแลระบบ และผู้จัดการตรวจสอบเท่านั้นที่แก้ไขได้</div>}
          {formErr && <div className="form-error" style={{ marginTop: 10 }}>{formErr}</div>}
        </div>
      </div>

      <div className="card card-pad" style={{ marginTop: 16 }}>
        <div className="row">
          <span className="card-label">บันทึกค่าคอมมิชชันจากใบแจ้งหนี้</span>
          <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>คำนวณจากยอดสุทธิ (ก่อน VAT) และรันซ้ำได้โดยไม่เพิ่มยอด</span>
        </div>
        <div className="row" style={{ gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
          <select className="select" style={{ minWidth: 300 }} value={selectedInvoice} disabled={invoices.length === 0 || !canManage}
            aria-label="ใบแจ้งหนี้"
            onChange={(e) => { setInvoiceID(e.target.value); setAccrual(null); setAccrueErr('') }}>
            {invoices.length === 0 && <option value="">ยังไม่มีใบแจ้งหนี้</option>}
            {invoices.map((inv) => (
              <option key={inv.id} value={inv.id}>
                {inv.number} · สุทธิ {moneyShort(inv.subtotal_satang)} · {inv.status === 'paid' ? 'ชำระแล้ว' : inv.status === 'void' ? 'ยกเลิก' : 'รอชำระ'}
              </option>
            ))}
          </select>
          <button className="btn btn-primary" disabled={!canManage || !selectedInvoice || accrue.isPending}
            onClick={() => { setAccrueErr(''); accrue.mutate(selectedInvoice) }}>
            {accrue.isPending ? 'กำลังบันทึก…' : 'บันทึกค่าคอมมิชชัน'}
          </button>
        </div>
        <div className="dim" style={{ fontSize: 11.5, marginTop: 8 }}>
          ระบบสร้างหนึ่งรายการต่อพาร์ทเนอร์ที่ใช้งานอยู่ · พาร์ทเนอร์ที่พักไว้จะไม่ได้ค่าคอมมิชชันจากใบใหม่
        </div>
        {accrueErr && <div className="form-error" style={{ marginTop: 10 }}>{accrueErr}</div>}
        {accrual && (
          <div style={{ marginTop: 12 }}>
            <div className="row" style={{ gap: 14, flexWrap: 'wrap', fontSize: 12.5 }}>
              <span className="chip gold">{accrual.invoice_number}</span>
              <span>ฐานคำนวณ <span className="mono tnum">{moneyShort(accrual.base_satang)}</span></span>
              <span className="dim">สร้างใหม่ {accrual.created} · มีอยู่แล้ว {accrual.skipped}</span>
            </div>
          </div>
        )}
      </div>

      <div className="card card-pad" style={{ marginTop: 16 }}>
        <div className="row">
          <span className="card-label">บัญชีค่าคอมมิชชัน</span>
          <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>{commissions.length} รายการล่าสุด</span>
        </div>
        {commissionsQ.isLoading ? (
          <div style={{ marginTop: 14 }}><Skel h={200} /></div>
        ) : commissions.length === 0 ? (
          <div style={{ marginTop: 14 }}>
            <Empty title="ยังไม่มีค่าคอมมิชชัน" body="เลือกใบแจ้งหนี้ด้านบนแล้วบันทึกค่าคอมมิชชันเพื่อเริ่มบัญชี" />
          </div>
        ) : (
          <div className="table-wrap" style={{ marginTop: 12 }}>
            <table className="data">
              <thead>
                <tr>
                  <th>พาร์ทเนอร์</th><th>ใบแจ้งหนี้</th><th className="num">ฐานสุทธิ</th>
                  <th className="num">อัตรา</th><th className="num">ค่าคอมมิชชัน</th><th>สถานะ</th><th>วันที่</th><th />
                </tr>
              </thead>
              <tbody>
                {commissions.map((c) => (
                  <tr key={c.id}>
                    <td><span className="mono">{c.partner_code}</span> <span className="dim">{c.partner_name}</span></td>
                    <td className="mono dim">{c.invoice_number}</td>
                    <td className="num tnum">{moneyShort(c.base_satang)}</td>
                    <td className="num tnum">{commissionPercent(c.commission_bp)}</td>
                    <td className="num tnum">{moneyShort(c.amount_satang)}</td>
                    <td>
                      <span className="chip" style={{ color: COMMISSION_TONE[c.status], borderColor: 'currentColor' }}>
                        {COMMISSION_LABEL[c.status] ?? c.status}
                      </span>
                    </td>
                    <td className="dim">{c.created_at ? thaiDate(c.created_at) : '—'}</td>
                    <td>
                      <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                        {canPay && c.status !== 'paid' && (
                          <button className="btn btn-ghost btn-sm" disabled={markPaid.isPending}
                            onClick={() => { setAccrueErr(''); markPaid.mutate(c.id) }}>
                            {markPaid.isPending ? 'กำลังบันทึก…' : 'ทำเครื่องหมายจ่ายแล้ว'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card card-pad" style={{ marginTop: 16 }}>
        <div className="row">
          <span className="card-label">สรุปยอดตามพาร์ทเนอร์</span>
          <span className="dim" style={{ marginLeft: 'auto', fontSize: 11.5 }}>ผลรวมเป็นจำนวนเต็มสตางค์</span>
        </div>
        {totalsView.lines.length === 0 ? (
          <div className="dim" style={{ fontSize: 12.5, marginTop: 12 }}>ยังไม่มีรายการให้สรุป</div>
        ) : (
          <div className="table-wrap" style={{ marginTop: 12 }}>
            <table className="data">
              <thead>
                <tr>
                  <th>พาร์ทเนอร์</th><th className="num">รายการ</th><th className="num">ค้างจ่าย</th>
                  <th className="num">อนุมัติแล้ว</th><th className="num">จ่ายแล้ว</th><th className="num">รวมสะสม</th>
                </tr>
              </thead>
              <tbody>
                {totalsView.lines.map((l) => (
                  <tr key={l.partner_id}>
                    <td><span className="mono">{l.partner_code}</span> <span className="dim">{l.partner_name}</span></td>
                    <td className="num tnum">{l.count}</td>
                    <td className="num tnum">{moneyShort(l.accrued_satang)}</td>
                    <td className="num tnum">{moneyShort(l.approved_satang)}</td>
                    <td className="num tnum">{moneyShort(l.paid_satang)}</td>
                    <td className="num tnum">{moneyShort(l.total_satang)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td>รวมทั้งสิ้น</td>
                  <td className="num tnum">{totalsView.count}</td>
                  <td className="num tnum">{moneyShort(totalsView.accrued)}</td>
                  <td className="num tnum">{moneyShort(totalsView.approved)}</td>
                  <td className="num tnum">{moneyShort(totalsView.paid)}</td>
                  <td className="num tnum">{moneyShort(totalsView.total)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      <div className="card card-pad" style={{ marginTop: 16 }}>
        <div className="card-label">หลักการคิดค่าคอมมิชชัน</div>
        <ul className="dim" style={{ fontSize: 12.5, lineHeight: 1.9, margin: '12px 0 0', paddingLeft: 18 }}>
          <li>ฐานคำนวณคือยอดสุทธิของใบแจ้งหนี้ก่อน VAT ไม่ใช่ยอดที่ลูกค้าจ่ายรวมภาษี</li>
          <li>อัตราเก็บเป็น basis points: 250 bp = 2.5% และปัดครึ่งขึ้นเฉพาะสตางค์สุดท้าย</li>
          <li>หนึ่งใบแจ้งหนี้มีค่าคอมมิชชันได้หนึ่งรายการต่อพาร์ทเนอร์ การรันซ้ำจึงไม่เพิ่มยอด</li>
          <li>รายการที่บันทึกแล้วไม่ถูกลบ แต่เปลี่ยนสถานะเป็นอนุมัติหรือจ่ายแล้วเท่านั้น</li>
          <li>การจ่ายซ้ำไม่มีผล ระบบคืนรายการเดิมโดยไม่สร้างยอดใหม่</li>
        </ul>
      </div>
    </>
  )
}

/** Satang -> "1,234.56" for table cells; the ฿ symbol is left to the stat cards. */
function moneyShort(satang: number): string {
  return (satang / 100).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}
